import {
  IconChevronRight,
  IconFuelefficiencyFill,
  IconGasstationFill,
  IconProfileFill,
  IconStarFill,
} from '@macarongfactory/web-icons'
import { Divider, Text } from '@macarongfactory/web-ui'

import RankBadge from './RankBadge'
import RankCarThumbnail from './RankCarThumbnail'

import type { IRankVehicle, TRankTab } from '../_features/rank'
import type { ReactNode } from 'react'

/** 지표 아이콘 크기(px) — Figma 16 */
const METRIC_ICON_SIZE_PX = 16
/** 모바일 우측 이동 chevron 크기(px) — Figma 16 */
const MOBILE_CHEVRON_SIZE_PX = 16
/** 유류비 금액 표기 단위 (만원) */
const FUEL_COST_UNIT = 10000

interface IRankMetricProps {
  icon: ReactNode
  text: string
}

/** 데스크탑 지표 1칸 (아이콘 + 값) — Figma Small Details Horizontal Layout */
const RankMetric = ({ icon, text }: IRankMetricProps) => (
  <div className='flex min-w-px flex-1 items-center gap-dimension-x0-5'>
    {icon}
    <Text
      variant='title-2-semibold'
      color='text-foreground-neutral-normal'
      className='whitespace-nowrap'
    >
      {text}
    </Text>
  </div>
)

const ProfileMetricIcon = () => (
  <span className='inline-flex text-foreground-neutral-assistive'>
    <IconProfileFill width={METRIC_ICON_SIZE_PX} height={METRIC_ICON_SIZE_PX} />
  </span>
)

interface IRankListItemProps {
  item: IRankVehicle
  tab: TRankTab
  /** 인앱: 우측 chevron 노출 (상세로 탭 이동 어피던스) */
  showChevron?: boolean
}

/**
 * 차종 랭킹 리스트 행
 * 좌: 순위 배지 + (모델명 / 지표) · 우: 차 썸네일.
 */
const RankListItem = ({ item, tab, showChevron }: IRankListItemProps) => {
  const modelLabel = `${item.brandName} ${item.modelName}`
  // 유류비순에서만 모델명 아래 유종명을 노출한다.
  const isFuelCost = tab === 'fuelCost'

  const primaryMetric: IRankMetricProps =
    tab === 'efficiency'
      ? {
          icon: (
            <span className='inline-flex text-foreground-success-strong'>
              <IconFuelefficiencyFill
                width={METRIC_ICON_SIZE_PX}
                height={METRIC_ICON_SIZE_PX}
              />
            </span>
          ),
          text: `${item.realFuelEfficiency.toFixed(2)}${item.fuelEfficiencyUnit}`,
        }
      : isFuelCost
        ? {
            icon: (
              <span className='inline-flex text-foreground-brand-strong'>
                <IconGasstationFill
                  width={METRIC_ICON_SIZE_PX}
                  height={METRIC_ICON_SIZE_PX}
                />
              </span>
            ),
            // 연간 유류비 → '약 999만원' (만원 단위 반올림)
            text: `약 ${Math.round(item.annualFuelCost / FUEL_COST_UNIT).toLocaleString()}만원`,
          }
        : {
            icon: (
              <span className='inline-flex text-light-amber-400'>
                <IconStarFill
                  width={METRIC_ICON_SIZE_PX}
                  height={METRIC_ICON_SIZE_PX}
                />
              </span>
            ),
            text: `${item.satisfactionScore.toFixed(2)}점`,
          }

  const ownerText = `${item.ownerCount.toLocaleString()}명`

  return (
    <>
      <article className='flex flex-col lg:hidden'>
        <div className='flex items-start gap-dimension-x2 bg-background-surface-default px-dimension-x4 pb-dimension-x4'>
          {/* 4위+ 라벨 정렬: 단일행(비-유류비)은 모델명이 하단 정렬이라 라벨도
              하단(h-52 = pt-x3 12 + 행 40)에 맞추고, 유류비 2행은 첫 행에 맞춰 pt-x3.5. */}
          <RankBadge
            rank={item.rank}
            medalPositionClassName={
              isFuelCost ? 'pt-dimension-x3-5' : 'h-[52px]'
            }
          />
          <div className='flex min-w-px flex-1 flex-col gap-dimension-x2 pt-dimension-x3'>
            <div
              className={`flex gap-dimension-x2 ${isFuelCost ? 'items-center' : 'h-[40px] items-end'}`}
            >
              {isFuelCost ? (
                <div className='flex min-w-px flex-1 flex-col gap-dimension-x1'>
                  <Text
                    variant='title-1-bold'
                    color='text-foreground-neutral-strong'
                    className='block w-full truncate'
                  >
                    {modelLabel}
                  </Text>
                  <Text
                    variant='caption-1-medium'
                    color='text-foreground-neutral-normal'
                    className='block w-full truncate'
                  >
                    {item.fuelTypeName}
                  </Text>
                </div>
              ) : (
                <Text
                  variant='title-1-bold'
                  color='text-foreground-neutral-strong'
                  className='min-w-px flex-1 truncate'
                >
                  {modelLabel}
                </Text>
              )}
              <div className='flex shrink-0 items-center gap-dimension-x2'>
                <RankCarThumbnail
                  imageUrls={item.imageUrls}
                  alt={modelLabel}
                  size='sm'
                />
                <span className='inline-flex shrink-0 text-foreground-neutral-assistive'>
                  <IconChevronRight
                    width={MOBILE_CHEVRON_SIZE_PX}
                    height={MOBILE_CHEVRON_SIZE_PX}
                  />
                </span>
              </div>
            </div>
            <div className='flex items-center gap-dimension-x2'>
              <div className='flex min-w-[100px] shrink-0 items-center gap-dimension-x0-5'>
                {primaryMetric.icon}
                <Text
                  variant='title-2-semibold'
                  color='text-foreground-neutral-strong'
                  className='whitespace-nowrap'
                >
                  {primaryMetric.text}
                </Text>
              </div>
              <Divider.Vertical height='x3' />
              <div className='flex min-w-px flex-1 items-center gap-dimension-x0-5'>
                <ProfileMetricIcon />
                <Text
                  variant='title-2-semibold'
                  color='text-foreground-neutral-normal'
                  className='whitespace-nowrap'
                >
                  {ownerText}
                </Text>
              </div>
            </div>
          </div>
        </div>
        <Divider.Horizontal />
      </article>

      {/* 데스크탑 — Figma 8462:102977 (카드) */}
      <article className='hidden items-end justify-between overflow-hidden rounded-dimension-x4 border border-stroke-neutral-strong bg-background-surface-default pb-dimension-x4 pl-dimension-x6 pr-dimension-x8 lg:flex'>
        <div className='flex min-w-px flex-1 items-start gap-dimension-x4'>
          <RankBadge rank={item.rank} />

          <div className='flex min-w-px flex-1 flex-col gap-dimension-x1'>
            <div className='flex w-full min-w-px flex-col gap-dimension-x1 overflow-hidden pt-dimension-x3-5'>
              <Text
                variant='title-1-bold'
                color='text-foreground-neutral-strong'
                className='block w-full truncate'
              >
                {modelLabel}
              </Text>
              {isFuelCost ? (
                <Text
                  variant='caption-1-medium'
                  color='text-foreground-neutral-normal'
                  className='block w-full truncate'
                >
                  {item.fuelTypeName}
                </Text>
              ) : null}
            </div>

            <div className='flex w-full max-w-[240px] items-center gap-dimension-x4'>
              <RankMetric icon={primaryMetric.icon} text={primaryMetric.text} />
              <Divider.Vertical height='x3' />
              <RankMetric icon={<ProfileMetricIcon />} text={ownerText} />
            </div>
          </div>
        </div>

        <div className='flex items-center gap-dimension-x2'>
          <RankCarThumbnail imageUrls={item.imageUrls} alt={modelLabel} />
          {showChevron ? (
            <span className='inline-flex shrink-0 text-background-overlay-strong'>
              <IconChevronRight width={20} height={20} />
            </span>
          ) : null}
        </div>
      </article>
    </>
  )
}

export default RankListItem
